import React, { useState, useEffect } from "react";
import {
  Voronoi,
  MarkSeries,
  Hint,
  FlexibleXYPlot,
  HorizontalGridLines,
  LineSeries,
  XAxis,
  YAxis,
} from "react-vis";
import { Col, Row } from "react-bootstrap";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";
import moment from "moment";
import UserService from "../services/user-service";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import QuestionMark from "../assets/Icon/QuestionMark";
import Popover from "@mui/material/Popover";
import * as d3 from "d3";
import "react-vis/dist/style.css";
import "../styling/sidebar.css";
import { Checkbox, FormControlLabel } from "@mui/material";
import { ExpenseTimeBreakdownChart } from "./Charts/ExpenseTrackerChart";

const ONE_DAY = 86400000;

function createData(name, date, amount, site, category, note) {
  return { name, date, amount, site, category, note };
}

function toX(value) {
  if (value && Object.keys(value).length > 0) {
    const date = new Date(value.x);
    return (
      date.toLocaleString("default", { month: "short" }) + " " + date.getDate()
    );
  }
}

function toY(value) {
  if (value && Object.keys(value).length > 0) {
    return "$" + value.y.toFixed(2);
  }
}

const rows = [
  createData(
    "Hand plane",
    "4/9/21",
    "$10.00",
    "Lowe's",
    "Workshop tools",
    "Replacement"
  ),
  createData("Oak 4 x 4 x 4", "4/21/21", "$37.00", "Lowe's", "Lumber", 0),
  createData(
    "Dado blade",
    "5/10/21",
    "$50.00",
    "Lowe's",
    "Workshop tools",
    "Drawer"
  ),
  createData("Sandpaper", "6/9/21", "$10.00", "Lowe's", "Supplies", "Bench"),
];

function AggregateLineChart(props) {
  const [, setToLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState<any[]>([]);
  const [nrows, setnRows] = useState(rows);
  const [value, setValue] = useState<any>({});
  const [color, setColor] = useState("#000000");
  const [open, setOpen] = useState(false);
  const [average, setAverage] = useState(0);
  const [showHint, setShowHint] = useState(true);
  const [xAxisLabel, setXaxisLabel] = useState("");
  const [filterDate, setFilterDate] = useState<any>([]);
  const [expenseCap, setExpenseCap] = useState(0);
  const [viewGoal, setViewGoal] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openModel = Boolean(anchorEl);
  var startDate = moment().subtract(2, "months").subtract(2,"days").toDate();
  var endDate = moment().subtract(1, "days").toDate();
  var xdate: any;
  var total = 0;

  const createData3 = (date, amount) => {
    total += amount;
    return { x: date.getTime(), y: amount };
  };

  useEffect(() => {
    UserService.getExpense().then((response) => {
      const filtered = UserService.getSpecificExpense(
        response.data.expense,
        props.stream + 1
      );
      if (props.expenseFilter === 0) {
        if (filtered.length === 0) {
          setShowHint(false);
        } else {
          setnRows(filtered);
          setShowHint(true);
        }
      } else if (props.expenseFilter === 1) {
        const weekStartDate = moment()
          .subtract(8, "days")
          .toDate();
        const weekEndDate = moment()
          .subtract(1, "days")
          .toDate();

        const lastWeekData = filtered.filter((item) => {
          return moment(item.date).isBetween(weekStartDate, weekEndDate);
        });

        if (lastWeekData.length <= 0) {
          toast.error("No data in last week");
          setShowHint(false);
          setnRows(rows);
        } else {
          setnRows(lastWeekData);
          setShowHint(true);
        }
      } else if (props.expenseFilter === 2) {
        const lastMonthStartDate = moment()
          .subtract(1, "months")
          .startOf("month")
          .toDate();
        const lastMonthEndDate = moment()
          .subtract(1, "months")
          .endOf("month")
          .toDate();

        const lastMonth = filtered.filter((item) => {
          return moment(item.date).isBetween(
            lastMonthStartDate,
            lastMonthEndDate
          );
        });
        if (lastMonth.length <= 0) {
          toast.error("No data in last month");
          setShowHint(false);
          setnRows(rows);
        } else {
          setnRows(lastMonth);
          setShowHint(true);
        }
      } else if (props.expenseFilter === 3) {
        const last60Days = filtered.filter((item) => {
          return moment(item.date).isBetween(filterDate[0], filterDate[1]);
        });
        setnRows(last60Days);
        if (last60Days.length <= 0) {
          toast.error("No data in last 60 days");
          setShowHint(false);
          setnRows(rows);
        } else {
          setnRows(last60Days);
          setShowHint(true);
        }
      }
      setToLoading(false);
    });
    setFilterDate([startDate, endDate]);
  }, [props.stream, props.expenseFilter,props.reloadAPI]);

  useEffect(() => {
    UserService.getIncome().then((response) => {
      var data =  response.data.expenseCapItem[props.stream];
      data !== undefined && setExpenseCap(data[0]);
    });
  }, [viewGoal, props.stream]);

  useEffect(() => {
    setViewGoal(true);
  }, [props.stream])

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // for average find
  var minDate = new Date();
  var dict = {};
  var maxValue = 0;
  var dateArray: Date[] = [];
  var numDays = 0;
  var tempDate = new Date();
  var amount_before = 0;

  useEffect(() => {
    let sum = 0;
    const averageCount = () => {
      Object.keys(dict).forEach((nrow) => {
        sum = sum + dict[nrow][1];
      });
      setAverage(sum / Object.keys(dict).length);
    };
    averageCount();
    Array.from(nrows).map(function (e) {
      setXaxisLabel(e.date);
    });
  }, [dict]);

  function createMarkSeriesData(data: any) {
    setHoveredNode([{ x: data.x, y: data.y }]);
  }

  Array.from(nrows).map(function (e) {
    tempDate = new Date(e.date);
    dateArray.push(tempDate);
  });

  const maxDateF = (dateArray: Date[]) =>
    new Date(Math.max(...dateArray.map(Number)));
  const minDateF = (dateArray: Date[]) =>
    new Date(Math.min(...dateArray.map(Number)));
  const maxDay = maxDateF(dateArray);
  const minDay = minDateF(dateArray);

  numDays = (maxDay.getTime() - minDay.getTime()) / ONE_DAY + 1;

  if (numDays > 12 && numDays <= 60) {
    numDays = 3;
  } else if (numDays > 60 && numDays < 365) {
    numDays = 6;
  } else if (numDays >= 365) {
    numDays = 12;
  }

  function myFormatter(xAxisLabel) {
    if (numDays === 1) {
      Array.from(nrows).map(function (e) {
        xdate = new Date(e.date);
      });
      const format = d3.timeFormat("%a");
      return props.expenseFilter === 1 || props.expenseFilter === 3
        ? format(xdate)
        : xdate.toLocaleString("default", { month: "short" }) +
            " " +
            xdate.getDate();
    } else {
      xdate = new Date(xAxisLabel);
      const format = d3.timeFormat("%a");
      return props.expenseFilter === 1 || props.expenseFilter === 3
        ? format(xdate)
        : xdate.toLocaleString("default", { month: "short" }) +
            " " +
            xdate.getDate();
    }
  }

  Array.from(nrows).map(function (e) {
    const amount = parseFloat(e.amount);
    const date = new Date(e.date);
    amount_before = 0;

    Array.from(nrows).map(function (e) {
      const curr_date = new Date(e.date);
      if (curr_date < date) {
        amount_before += parseFloat(e.amount);
      }
    });

    if (date < minDate) {
      minDate = date;
    }
    if (e.date in dict) {
      dict[e.date] = [date, dict[e.date][1] + amount + amount_before];
    } else {
      dict[e.date] = [date, amount + amount_before];
    }
    if (dict[e.date][1] > maxValue) {
      maxValue = dict[e.date][1];
    }
  });

  if (nrows.length === 0) {
    return <div></div>;
  }

  // requires that expenses are sorted by date
  let list = Object.keys(dict)
    .map((nrow) => createData3(dict[nrow][0], dict[nrow][1]))
    .sort((data_a, data_b) => data_a.x - data_b.x);

  // for user color change
  const handleChangeComplete = (color) => {
    let colorStr = color.hex
    if(color.rgb.a !== 1){
      colorStr = `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
    }
    setColor(colorStr);
  };

  return (
    <Row className="mt-4 w-100 justify-content-center">
      <Col xs={8} className="d-flex justify-content-center ">
        <div className="w-100">
          <h4 className="graph-header">View Expenses Over Time</h4>
          <h5 className="graph-title">
            [ Drag and drop the line graph for Zooming ]
          </h5>
          <div className="w-100">
            <ExpenseTimeBreakdownChart
              categories={list.map((d) => myFormatter(d.x)) || []}
              data={list.map((d) => +d.y.toFixed()) || []}
              average={average}
              color={color}
              stream={props.stream}
              viewGoal={viewGoal}
              expenseCap={expenseCap}
            />
            {/*<FlexibleXYPlot*/}
            {/*  yDomain={[0, maxValue * 1.2]}*/}
            {/*  width={750}*/}
            {/*  height={400}*/}
            {/*  margin={{ left: 55, right: 0, top: 50, bottom: 70 }}*/}
            {/*  onMouseLeave={() => {*/}
            {/*    setValue({});*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <HorizontalGridLines />*/}
            {/*  <XAxis*/}
            {/*    title={*/}
            {/*      props.expenseFilter === 1 || props.expenseFilter === 3*/}
            {/*        ? "Time (days)"*/}
            {/*        : "Time (months)"*/}
            {/*    }*/}
            {/*    tickTotal={numDays}*/}
            {/*    tickFormat={myFormatter}*/}
            {/*    style={{*/}
            {/*      title: {*/}
            {/*        fontSize: "16px",*/}
            {/*        fontWeight: 600,*/}
            {/*      },*/}
            {/*    }}*/}
            {/*  />*/}
            {/*  <YAxis*/}
            {/*    title="Expenses ($)"*/}
            {/*    style={{*/}
            {/*      title: {*/}
            {/*        fontSize: "16px",*/}
            {/*        fontWeight: 600,*/}
            {/*        transform: "translate(24px,-9px)",*/}
            {/*      },*/}
            {/*    }}*/}
            {/*  />*/}
            {/*  <LineSeries*/}
            {/*    color={color}*/}
            {/*    data={Object.keys(dict)*/}
            {/*      .map((nrow) => createData3(dict[nrow][0], dict[nrow][1]))*/}
            {/*      .sort((data_a, data_b) => data_a.x - data_b.x)}*/}
            {/*    onNearestXY={(v) => {*/}
            {/*      setValue({ x: v.x, y: v.y } || {});*/}
            {/*      setColor(value.y > average ? "red" : "green");*/}
            {/*    }}*/}
            {/*  />*/}
            {/*  {viewGoal && expenseCap !== 0 && (*/}
            {/*    <LineSeries*/}
            {/*      color="#4BCA89"*/}
            {/*      data={*/}
            {/*        list?.length*/}
            {/*          ? list?.map(({ x, y }) => ({ x, y: expenseCap }))*/}
            {/*          : []*/}
            {/*      }*/}
            {/*      strokeStyle="dashed"*/}
            {/*      style={{ strokeWidth: 2 }}*/}
            {/*    />*/}
            {/*  )}*/}
            {/*  <MarkSeries data={hoveredNode} style={{ fill: "green" }} />*/}
            {/*  <Voronoi*/}
            {/*    extent={[*/}
            {/*      [0, 0],*/}
            {/*      [700, 350],*/}
            {/*    ]}*/}
            {/*    nodes={list}*/}
            {/*    onHover={(node: any) => {*/}
            {/*      createMarkSeriesData(node);*/}
            {/*    }}*/}
            {/*    onBlur={() => setHoveredNode([])}*/}
            {/*  />*/}
            {/*  {Object.keys(value).length !== 0 && (*/}
            {/*    <Hint*/}
            {/*      value={value}*/}
            {/*      align={{ horizontal: "right", vertical: "bottom" }}*/}
            {/*    >*/}
            {/*      <div className="hint-box">*/}
            {/*        <div className="hint-title">*/}
            {/*          <div className="hint-date">{toX(value)}</div>*/}
            {/*          <div*/}
            {/*            className={*/}
            {/*              value.y > average ? "higher-amount" : "lower-amount"*/}
            {/*            }*/}
            {/*          >*/}
            {/*            {toY(value)}*/}
            {/*          </div>*/}
            {/*        </div>*/}
            {/*        <div className="hint-body">*/}
            {/*          Your expense is*/}
            {/*          <span*/}
            {/*            className={*/}
            {/*              value.y > average ? "higher-amount" : "lower-amount"*/}
            {/*            }*/}
            {/*          >*/}
            {/*            {value.y > average ? "higher" : "lower"}*/}
            {/*          </span>*/}
            {/*          than average:*/}
            {/*          <span className="hint-result">*/}
            {/*            {`$${Math.abs(Number((average - value.y).toFixed(2)))}`}*/}
            {/*          </span>*/}
            {/*        </div>*/}
            {/*      </div>*/}
            {/*    </Hint>*/}
            {/*  )}*/}
            {/*</FlexibleXYPlot>*/}
            <div className="d-flex justify-content-center ml-2">
              {expenseCap !== 0 && (
                <div className="d-flex align-items-center mr-3">
                  <div className="mt-2">
                    <FormControlLabel
                      value="viewGoal"
                      control={<Checkbox />}
                      label="View goal progress"
                      labelPlacement="end"
                      onChange={() => setViewGoal(!viewGoal)}
                      className="view-goal-checkbox"
                      checked={viewGoal}
                    />
                  </div>
                </div>
              )}
              <div className="sketch-color">
                {open && (
                  <SketchPicker
                    className="sketch-picker"
                    color={color}
                    onChangeComplete={handleChangeComplete}
                  />
                )}
              </div>
              <div className="d-flex justify-content-center align-items-center my-3 color-picker">
                <span
                  onClick={() => setOpen(!open)}
                  style={{ backgroundColor: color }}
                  className="card-color"
                ></span>
                <span
                  onClick={() => setOpen(!open)}
                  style={{ color: color, marginLeft: 10 }}
                >
                  Expense
                </span>
              </div>
              <div className="my-3">
                <Box>
                  <Typography
                    aria-owns={openModel ? "mouse-over-popover" : undefined}
                    aria-haspopup="true"
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                  >
                    <QuestionMark className="ml-3" />
                  </Typography>
                  <Popover
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: "none",
                    }}
                    open={openModel}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    className="ml-2"
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }} className="text-white bg-dark">
                      Click the box to select a color
                    </Typography>
                  </Popover>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}
export default AggregateLineChart;
